/**
 * The single shared dose formatter. Every dose in the interface goes through
 * here.
 *
 * **Patient safety requirement, not a presentation preference.** A comma as
 * decimal separator in a medication list is a factor-of-ten error risk: a
 * Swedish reader sees `0,25 mg` and a reader from almost anywhere else may read
 * the same glyphs as twenty-five. So the decimal separator is a point in every
 * interface language, and the other five rules in docs/I18N.md §4 are enforced
 * here rather than trusted to each caller.
 *
 * | Rule | Right | Wrong |
 * |---|---|---|
 * | Decimal point always, regardless of language | `0.5 mg` | `0,5 mg` |
 * | Leading zero required | `0.5 mg` | `.5 mg` |
 * | Trailing zero forbidden | `5 mg` | `5.0 mg` |
 * | No thousands separators | `1000 mg` | `1 000 mg` |
 * | Metric units only, `IE` excepted (#34) | `mg`, `mL`, `kg`, `IE` | `lbs` |
 * | `mikrogram` spelled out | `mikrogram` | `µg`, `ug`, `mcg` |
 * | Unit always accompanies the value | `5 mg` | `5` |
 *
 * The trailing-zero rule is the one that looks like tidiness and is not. `5.0`
 * read through a smudge, a low-resolution print or a scanned fax is `50`; the
 * decimal point is the least robust glyph on the page, so a dose must never
 * depend on it being seen when it carries no information.
 *
 * `toLocaleString` is forbidden on clinical values (AGENTS.md §3 rule 7) for the
 * same reason it is forbidden on dates: it is locale-dependent, host-dependent,
 * and has changed output between runtime versions. Under a Swedish locale it
 * produces exactly the comma this module exists to prevent.
 *
 * See docs/I18N.md §4 and AGENTS.md §5. Sibling of ./clinicalDate.ts.
 */

/**
 * Thrown rather than rendering a dose that might be misread.
 *
 * Every other module in a UI would degrade: render a dash, render the raw
 * value, render nothing. None of those is available here. A blank where a dose
 * belongs reads as *no dose*, and a raw value reads as a dose that was
 * formatted. Failing loudly is the only option that cannot be misread, and the
 * screen above the fold is the one place in this system where being wrong is
 * worse than being absent.
 */
export class InvalidClinicalDoseError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'InvalidClinicalDoseError'
  }
}

/**
 * Spellings of microgram that must never reach a screen, mapped to the one that
 * must.
 *
 * `µg` is the classic ten-fold and thousand-fold error: handwritten or in a
 * small font it is `mg`, and `mcg` is not universally read either. Swedish
 * practice spells it out, and so does this system — the word is long precisely
 * so that it cannot be confused with anything.
 *
 * Both micro signs are here on purpose. U+00B5 MICRO SIGN and U+03BC GREEK
 * SMALL LETTER MU look identical and arrive from different keyboards and
 * different export tools, so checking for one of them catches half the cases.
 */
const FORBIDDEN_MICROGRAM = new Set(['µg', 'μg', 'ug', 'mcg', 'mikrog', 'µgram', 'μgram'])

/**
 * Units that are not metric. Not an exhaustive list of non-metric units — an
 * exhaustive list is not possible — but the ones that plausibly arrive from a
 * US-derived source, which is exactly what our own test data is transformed
 * from.
 *
 * `gr` is here deliberately even though somebody may have meant gram: *grain*
 * is a real unit and 1 gr is about 65 mg, so the ambiguity is itself the hazard.
 * The metric spelling is `g`.
 */
const NON_METRIC = new Set([
  'lb',
  'lbs',
  'pound',
  'pounds',
  'oz',
  'ounce',
  'ounces',
  'gr',
  'grain',
  'grains',
  'tsp',
  'teaspoon',
  'tbsp',
  'tablespoon',
  'drop',
  'drops',
  'gtt',
  'fl oz',
])

/**
 * The amount as digits, with no trailing zeros and no exponent.
 *
 * A string amount is normalised textually rather than through `Number`, because
 * `dose_amount` is `numeric` in Postgres and a round trip through a float is
 * exactly the kind of silent change this module exists to prevent. `0.1` is not
 * representable in binary floating point; a `numeric` column is the right type
 * for a dose and a string is the right way to carry one across JSON.
 */
function normaliseAmount(amount: number | string): string {
  let text: string

  if (typeof amount === 'number') {
    if (!Number.isFinite(amount)) {
      throw new InvalidClinicalDoseError(`Dose amount is not a finite number: ${String(amount)}`)
    }
    text = String(amount)
    if (text.includes('e') || text.includes('E')) {
      // 1e-7 and 1e+21. Rendering either would need a decision about precision
      // that the caller is better placed to make, and a dose small enough to
      // reach exponent notation is a sign the unit is wrong — 0.000025 g is
      // 25 mikrogram. Pass the value as a string if it is genuinely intended.
      throw new InvalidClinicalDoseError(
        `Dose amount is in exponent notation and cannot be rendered safely: ${text}. ` +
          `Pass it as a string, or use a smaller unit.`,
      )
    }
  } else {
    text = amount.trim()
  }

  if (text === '') {
    throw new InvalidClinicalDoseError('Dose amount is empty.')
  }

  // A comma here means something upstream already formatted the number, and
  // guessing whether it was a decimal separator or a thousands separator is
  // precisely the factor-of-1000 mistake this module prevents. Same for a space.
  if (text.includes(',')) {
    throw new InvalidClinicalDoseError(
      `Dose amount contains a comma: ${text}. A dose is carried as digits with a decimal point; ` +
        `a comma is either a decimal separator or a thousands separator and this cannot tell which.`,
    )
  }

  // At least one digit before the point, so `.5` is rejected rather than
  // repaired. I18N §4's leading-zero rule is about what reaches the screen, and
  // requiring a digit here is what makes it true by construction below: the
  // leading-zero strip cannot eat the last one. A bare `.5` in a caller is a bug
  // somewhere upstream — a dose read out of `numeric` never looks like that — and
  // silently rendering `0.5` would hide it.
  if (!/^\d+(\.\d+)?$/.test(text)) {
    throw new InvalidClinicalDoseError(
      `Dose amount is not a plain positive decimal: ${text}. ` +
        `Expected digits, optionally one point and more digits — no sign, no separators, no unit, ` +
        `and a digit before the point.`,
    )
  }

  // Trailing zeros after the point carry no information and cost robustness:
  // `5.0` misread is `50`. Then a bare trailing point, from `5.` or `5.000`.
  if (text.includes('.')) {
    text = text.replace(/0+$/, '').replace(/\.$/, '')
  }

  // Leading zeros before a digit go: `05` reads as a typo or as part of an
  // identifier. The lookahead is what keeps `0.25` intact and leaves a lone `0`
  // for the check below to reject.
  text = text.replace(/^0+(?=\d)/, '')

  // migration 0006: medication_request_dose_positive CHECK (dose_amount > 0).
  // The database will not hold a zero, so a zero here is a bug in the caller
  // rather than a value to render.
  if (/^0(\.0*)?$/.test(text)) {
    throw new InvalidClinicalDoseError(
      `Dose amount is zero: ${text}. A dose of nothing is an omission, not an order.`,
    )
  }

  return text
}

/** The unit, checked for the spellings that must never reach a screen. */
function checkUnit(unit: string): string {
  const trimmed = unit.trim()

  if (trimmed === '') {
    // I18N §4: the unit always accompanies the value. `5` on a medication list
    // is 5 of something, and the reader supplies the unit from memory.
    throw new InvalidClinicalDoseError('Dose unit is missing. A number with no unit is not a dose.')
  }

  const lower = trimmed.toLowerCase()

  if (FORBIDDEN_MICROGRAM.has(lower)) {
    throw new InvalidClinicalDoseError(
      `Dose unit "${trimmed}" is an abbreviation of microgram. Use "mikrogram", spelled out — ` +
        `I18N.md §4. µg is misread as mg, which is a thousand-fold error.`,
    )
  }

  if (NON_METRIC.has(lower)) {
    throw new InvalidClinicalDoseError(
      `Dose unit "${trimmed}" is not metric. I18N.md §4 allows metric units only.`,
    )
  }

  // Anything else passes through unchanged, and that is deliberate — a denylist,
  // not an allowlist, decided in DECISIONS #34. This function's failure mode is a
  // thrown error where a dose should be, and I18N.md §5 puts the dose panel above
  // the fold on every patient screen. So an allowlist missing one legitimate unit
  // does not produce a wrong dose, it produces *no dose*, on the screen where that
  // matters most, the first time somebody prescribes something unusual.
  //
  // `IE` (internationella enheter) is why: insulin, heparin and vitamin D are
  // dosed by biological activity, there is no metric quantity to convert to, and
  // an allowlist would have made them unrenderable. #34 names it as the one
  // exception to §4's "metric units only" — the rule is aimed at localised and
  // imperial units, not at activity units.
  //
  // The cost is real and accepted there: an unexpected *wrong* unit also renders
  // as written. Constraining the vocabulary properly belongs in the database,
  // where migration 0006 currently checks only that dose_unit is non-empty, and it
  // waits on a clinically reviewed list.
  return trimmed
}

/**
 * A dose as `<amount> <unit>` — `0.5 mg`, `5 mg`, `100 mikrogram`, `1 g`.
 *
 * Pass `dose_amount` as a string when it comes from the API, so that the
 * `numeric` column's value is not routed through a float on the way to a
 * screen.
 *
 * Throws {@link InvalidClinicalDoseError} rather than rendering anything that
 * could be misread.
 */
export function formatDose(amount: number | string, unit: string): string {
  // The unit is checked first: of the two, a wrong unit is the bigger error, and
  // a caller fixing one message at a time should see it first.
  const checkedUnit = checkUnit(unit)
  return `${normaliseAmount(amount)} ${checkedUnit}`
}
